import { useEffect, useRef, type RefObject } from "react";

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function useDialogFocus(open: boolean, returnFocusRef?: RefObject<HTMLElement | null>) {
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const returnFocusTarget = returnFocusRef?.current ?? previouslyFocused;
    const previousOverflow = document.body.style.overflow;
    const modalLayer = Array.from(document.body.children).find((element) => element.contains(dialog));
    const backgroundStates = Array.from(document.body.children).filter((element) => element !== modalLayer).map((element) => ({
      element: element as HTMLElement,
      ariaHidden: element.getAttribute("aria-hidden"),
      inert: element.hasAttribute("inert"),
    }));
    document.body.style.overflow = "hidden";
    for (const state of backgroundStates) {
      state.element.setAttribute("inert", "");
      state.element.setAttribute("aria-hidden", "true");
    }
    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));
    (focusable[0] ?? dialog).focus();

    function containFocus(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const currentFocusable = Array.from(dialog!.querySelectorAll<HTMLElement>(focusableSelector));
      if (currentFocusable.length === 0) { event.preventDefault(); dialog!.focus(); return; }
      const first = currentFocusable[0]!;
      const last = currentFocusable[currentFocusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    document.addEventListener("keydown", containFocus);
    return () => {
      document.removeEventListener("keydown", containFocus);
      document.body.style.overflow = previousOverflow;
      for (const state of backgroundStates) {
        if (!state.inert) state.element.removeAttribute("inert");
        if (state.ariaHidden === null) state.element.removeAttribute("aria-hidden");
        else state.element.setAttribute("aria-hidden", state.ariaHidden);
      }
      returnFocusTarget?.focus();
    };
  }, [open, returnFocusRef]);

  return dialogRef;
}
